import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Mail, X } from 'lucide-react';
import Footer from '../components/Footer';
import { useContent } from '../content';
import { useLang } from '../i18n';
import { useViewContext } from '../viewContext';
import { ThemeToggle } from '../components/ThemeToggle';

const MeetTheTeam = () => {
  const navigate = useNavigate();
  const { team } = useContent();
  const { t } = useLang();
  const { setView } = useViewContext();
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    setView('The visitor is on the "Meet the Team" page, viewing the Intervantis team members.');
    return () => setView('');
  }, [setView]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (lightbox) setLightbox(null);
      else navigate('/');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, lightbox]);

  const goHomeTab = (tab) => navigate('/', { state: { tab } });

  return (
    <div className="min-h-screen flex flex-col team-page-bg" style={{ background: 'linear-gradient(180deg, #0c2b31 0%, #082129 55%, #06191f 100%)' }}>
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-navy/90 backdrop-blur-xl border-b border-navy/10 dark:border-white/10">
        <div className="max-w-[1680px] mx-auto px-6 md:px-10 h-[84px] flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="/brand/intervantis-horizontal-dark.png" alt="Intervantis" className="h-[46px] md:h-[52px] w-auto object-contain dark:brightness-0 dark:invert" draggable="false" />
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button onClick={() => navigate('/')} className="group inline-flex items-center gap-2.5 rounded-full bg-ember hover:bg-ember-hover text-white px-5 py-3 label-mono shadow-lg shadow-ember/20 transition-colors">
              <ArrowLeft size={17} strokeWidth={2.2} className="transition-transform group-hover:-translate-x-1" />
              {t('ui.backToSite')}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 relative">
        <div className="absolute inset-0 pointer-events-none team-glow" style={{ background: 'radial-gradient(90% 55% at 30% 0%, rgba(31,94,140,0.20) 0%, rgba(0,0,0,0) 60%)' }} />
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-24 relative">
          <div className="label-mono text-ember mb-6">{team.label}</div>
          <h1 className="font-display font-light text-white text-5xl md:text-6xl leading-[1.02] tracking-tight">{team.title}</h1>
          <div className="mt-5 label-mono text-white/50">{team.meta}</div>
          <div className="mt-6 w-16 h-px bg-ember/70" />

          <h2 className="mt-16 font-display font-medium text-white text-2xl md:text-3xl">{team.sectionTitle}</h2>
          <p className="mt-5 text-white/65 font-body text-base md:text-[17px] leading-relaxed">{team.intro}</p>

          {/* Cards */}
          <div className="mt-10 space-y-5">
            {team.members.map((m) => (
              <div
                key={m.no}
                data-testid={`team-member-${m.no}`}
                className="team-card-bg rounded-2xl border border-white/10 p-6 md:p-7 flex items-start gap-6 transition-colors duration-300 hover:border-ember/40"
                style={{ background: 'linear-gradient(100deg, rgba(47,110,120,0.35) 0%, rgba(12,42,49,0.55) 65%)' }}
              >
                <div
                  className={`shrink-0 w-[74px] h-[74px] rounded-full flex items-center justify-center font-display font-semibold text-2xl text-navy shadow-lg overflow-hidden ${m.photoFull ? 'cursor-pointer ring-2 ring-transparent hover:ring-ember/60 transition-all' : ''}`}
                  style={{ background: 'linear-gradient(145deg, #f7a24a 0%, #ef7b24 100%)' }}
                  onClick={() => m.photoFull && setLightbox({ src: m.photoFull, name: m.name })}
                  data-testid={m.photoFull ? `team-avatar-${m.no}` : undefined}
                >
                  {m.photo ? (
                    <img src={m.photo} alt={m.name} className="w-full h-full object-cover" draggable="false" />
                  ) : (
                    m.initials
                  )}
                </div>
                <div className="min-w-0">
                  <div className="label-mono text-ember mb-1.5">{m.no} · {t('ui.team')}</div>
                  <h3 className="font-display font-medium text-white text-2xl md:text-[28px] leading-tight">{m.name}</h3>
                  <div className="mt-1.5 label-mono text-white/50">{m.role}</div>
                  {m.bio && (
                    <div className="mt-4 space-y-4">
                      {m.bio.split('\n\n').map((para, idx) => (
                        <p key={idx} className="text-white/70 font-body text-[15px] leading-relaxed text-justify">{para}</p>
                      ))}
                    </div>
                  )}
                  {m.email && (
                    <a
                      href={`mailto:${m.email}`}
                      className="mt-4 inline-flex items-center gap-2 border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] px-3.5 py-2 rounded-full font-body text-sm text-white/80 transition-colors"
                    >
                      <Mail size={14} className="text-ember" />
                      {m.email}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer onTabChange={goHomeTab} />

      {lightbox && (
        <div
          data-testid="team-lightbox"
          className="fixed inset-0 z-[120] bg-navy-deep/95 backdrop-blur-sm flex items-center justify-center p-4 animate-msg-in"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="team-lightbox-close"
            onClick={() => setLightbox(null)}
            className="absolute top-5 right-5 w-11 h-11 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <X size={26} />
          </button>
          <figure className="max-w-[92vw] max-h-[88vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.src}
              alt={lightbox.name}
              className="max-w-[92vw] max-h-[80vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
              draggable="false"
            />
            <figcaption className="mt-4 label-mono text-white/70 text-sm">{lightbox.name}</figcaption>
          </figure>
        </div>
      )}
    </div>
  );
};

export default MeetTheTeam;
